"use strict";

import _defineProperty from "@babel/runtime-corejs3/helpers/defineProperty";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.promise.js";
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.string.includes.js";
import { CacheManager } from './utils/cache-manager';
import { ManifestLoader } from './utils/manifest-loader';
import { getFullTranslationPath, resolvePublicPath } from './utils/path-resolver';
import { PerformanceMonitor } from './utils/performance-monitor';
export let ManifestAwareBackend = /*#__PURE__*/function () {
  function ManifestAwareBackend() {
    _defineProperty(this, "type", 'backend');
    _defineProperty(this, "publicPath", '');
    _defineProperty(this, "enableFallback", true);
    _defineProperty(this, "retryAttempts", 3);
    _defineProperty(this, "retryDelay", 1000);
  }
  var _proto = ManifestAwareBackend.prototype;
  _proto.init = function init(_services, backendOptions, _i18nextOptions) {
    // Resolve public path for the current environment
    this.publicPath = resolvePublicPath({
      appName: backendOptions === null || backendOptions === void 0 ? void 0 : backendOptions.appName,
      publicPath: backendOptions === null || backendOptions === void 0 ? void 0 : backendOptions.publicPath,
      isDevelopment: typeof (process.env.NODE_ENV !== "production") !== 'undefined' && process.env.NODE_ENV !== "production"
    });

    // Initialize cache manager (memory-only for runtime performance)
    this.cacheManager = new CacheManager({
      maxMemoryCacheSize: backendOptions === null || backendOptions === void 0 ? void 0 : backendOptions.maxMemoryCacheSize
    });

    // Initialize performance monitor
    this.performanceMonitor = new PerformanceMonitor((backendOptions === null || backendOptions === void 0 ? void 0 : backendOptions.enablePerformanceMonitoring) !== false);

    // Initialize manifest loader
    this.manifestLoader = new ManifestLoader(this.publicPath, this.cacheManager);

    // Configuration options
    this.enableFallback = (backendOptions === null || backendOptions === void 0 ? void 0 : backendOptions.enableFallback) !== false;
    this.retryAttempts = (backendOptions === null || backendOptions === void 0 ? void 0 : backendOptions.retryAttempts) || 3;
    this.retryDelay = (backendOptions === null || backendOptions === void 0 ? void 0 : backendOptions.retryDelay) || 1000;
    if (__DEBUG__) {
      console.log('[i18n] ManifestAwareBackend initialized in DEVELOPMENT mode');
      console.log('[i18n] Loading unhashed files from /locales/');
    }

    // Start loading master manifest immediately (non-blocking)
    this.manifestLoader.loadMasterManifest();
  };
  _proto.read = function read(language, namespace, callback) {
    // Wrap in async IIFE to handle async operations in sync method
    (async () => {
      const startTime = this.performanceMonitor.startTimer();
      const cacheKey = `${language}_${namespace}`;
      try {
        // Development mode: bypass manifest system
        if (typeof (process.env.NODE_ENV !== "production") !== 'undefined' && process.env.NODE_ENV !== "production") {
          await this.loadDevelopment(language, namespace, callback, startTime);
          return;
        }

        // Production mode: check cache first
        const cached = await this.cacheManager.get(cacheKey);
        if (cached) {
          callback(null, cached.data);
          this.performanceMonitor.endTimer(startTime, `${cacheKey}_cached`);
          // Verify cache validity in background
          this.verifyCacheInBackground(language, namespace, cached);
          return;
        }

        // Load via manifest system
        await this.loadProduction(language, namespace, callback, startTime);
      } catch (error) {
        if (__DEBUG__) {
          console.error(`[i18n] Failed to load ${language}/${namespace}:`, error);
        }

        // Try fallback if enabled
        if (this.enableFallback && language !== 'en') {
          await this.loadWithFallback(language, namespace, callback);
        } else {
          callback(error, false);
        }
      }
    })();
  }

  /**
   * Load translations in development mode (unhashed files)
   */;
  _proto.loadDevelopment = async function loadDevelopment(language, namespace, callback, startTime) {
    const devPath = `/locales/${language}/${namespace}.json`;
    try {
      const data = await this.fetchWithRetry(devPath);
      callback(null, data);
      const duration = this.performanceMonitor.endTimer(startTime, `${language}_${namespace}_dev`);
      if (__DEBUG__) {
        console.log(`[i18n] Loaded ${language}/${namespace} in ${duration}ms`);
      }
    } catch (error) {
      if (__DEBUG__) {
        console.error(`[i18n] Dev mode: failed to load ${devPath}:`, error);
      }
      callback(error, false);
    }
  }

  /**
   * Load translations in production mode (via manifest)
   */;
  _proto.loadProduction = async function loadProduction(language, namespace, callback, startTime) {
    // Ensure manifest is loaded
    await this.manifestLoader.loadMasterManifest();

    // Check if this app has translations at all
    if (!this.manifestLoader.hasTranslations()) {
      // Return empty object for apps without translations
      callback(null, {});
      return;
    }

    // Get the locale manifest
    const manifest = await this.manifestLoader.loadLocaleManifest(language);
    if (!manifest) {
      // Fallback to direct loading if manifest not available
      if (this.enableFallback) {
        await this.loadDirectPath(language, namespace, callback, startTime);
        return;
      }
      callback(new Error(`No manifest available for ${language}`), false);
      return;
    }

    // Get translation info from manifest
    const translationInfo = this.manifestLoader.getTranslationInfo(language, namespace);
    if (!translationInfo) {
      callback(new Error(`Namespace ${namespace} not found in manifest for ${language}`), false);
      return;
    }

    // Fetch the versioned translation file
    const data = await this.fetchWithRetry(translationInfo.path);

    // Cache the loaded data
    const cachedData = {
      data,
      hash: translationInfo.hash,
      timestamp: Date.now()
    };
    await this.cacheManager.set(`${language}_${namespace}`, cachedData);
    callback(null, data);
    this.performanceMonitor.logTranslationLoad(language, namespace, this.performanceMonitor.endTimer(startTime, `${language}_${namespace}_network`), false);
  }

  /**
   * Load translations directly without manifest (fallback)
   */;
  _proto.loadDirectPath = async function loadDirectPath(language, namespace, callback, startTime) {
    const fallbackPath = `/locales/${language}/${namespace}.json`;
    try {
      const data = await this.fetchWithRetry(fallbackPath);

      // Cache without hash
      await this.cacheManager.set(`${language}_${namespace}`, {
        data,
        timestamp: Date.now()
      });
      callback(null, data);
      this.performanceMonitor.endTimer(startTime, `${language}_${namespace}_fallback`);
    } catch (error) {
      callback(error, false);
    }
  }

  /**
   * Load with English fallback
   */;
  _proto.loadWithFallback = async function loadWithFallback(language, namespace, callback) {
    if (__DEBUG__) {
      console.warn(`[i18n] Falling back from ${language} to en for namespace ${namespace}`);
    }

    // Try to load English version
    const manifest = await this.manifestLoader.loadLocaleManifest('en');
    if (!manifest) {
      callback(new Error('No English manifest available for fallback'), false);
      return;
    }
    const translationInfo = this.manifestLoader.getTranslationInfo('en', namespace);
    if (!translationInfo) {
      callback(new Error(`Namespace ${namespace} not found in English manifest`), false);
      return;
    }
    try {
      const data = await this.fetchWithRetry(translationInfo.path);

      // Cache as fallback
      await this.cacheManager.set(`${language}_${namespace}`, {
        data,
        hash: translationInfo.hash,
        timestamp: Date.now(),
        isFallback: true
      });
      callback(null, data);
    } catch (error) {
      callback(error, false);
    }
  }

  /**
   * Fetch with retry logic
   */;
  _proto.fetchWithRetry = async function fetchWithRetry(path, attempt = 1) {
    try {
      let fullPath = getFullTranslationPath(this.publicPath, path);

      // For hashed files, use default cache behavior (respects Cache-Control headers)
      // For non-hashed files (dev mode), always fetch fresh with timestamp
      // Check if file has hash pattern (namespace.hash.json) vs simple pattern (namespace.json)
      const isHashed = /\.[a-f0-9]{8}\.json$/.test(path);

      // In development, add timestamp to bypass any caching layers
      if (!isHashed && typeof (process.env.NODE_ENV !== "production") !== 'undefined' && process.env.NODE_ENV !== "production") {
        const separator = fullPath.includes('?') ? '&' : '?';
        fullPath = `${fullPath}${separator}t=${Date.now()}`;
      }
      const cacheMode = isHashed ? 'default' : 'no-cache';
      const response = await fetch(fullPath, {
        cache: cacheMode,
        credentials: 'same-origin'
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      if (attempt < this.retryAttempts) {
        if (__DEBUG__) {
          console.warn(`[i18n] Retry ${attempt}/${this.retryAttempts} for ${path}`);
        }
        await new Promise(resolve => setTimeout(resolve, this.retryDelay * attempt));
        return this.fetchWithRetry(path, attempt + 1);
      }
      throw error;
    }
  }

  /**
   * Verify cache validity in background
   */;
  _proto.verifyCacheInBackground = async function verifyCacheInBackground(language, namespace, cached) {
    // Skip verification if no hash or if it's a fallback
    if (!cached.hash || cached.isFallback) return;
    try {
      const translationInfo = this.manifestLoader.getTranslationInfo(language, namespace);

      // If hash matches, cache is valid
      if (translationInfo && cached.hash === translationInfo.hash) {
        return;
      }

      // Cache is stale, fetch new version
      if (translationInfo) {
        const fullPath = getFullTranslationPath(this.publicPath, translationInfo.path);
        const response = await fetch(fullPath, {
          cache: 'force-cache'
        });
        if (response.ok) {
          const newData = await response.json();
          await this.cacheManager.set(`${language}_${namespace}`, {
            data: newData,
            hash: translationInfo.hash,
            timestamp: Date.now()
          });
        }
      }
    } catch (error) {
      // Silent failure - this is a background operation
      if (__DEBUG__) {
        console.debug('[i18n] Background cache verification failed:', error);
      }
    }
  }

  // Required for i18next BackendModule interface (not used in production)
  ;
  _proto.create = function create(_languages, _namespace, _key, _fallbackValue) {
    // Not implemented for production
  };
  _proto.save = function save(_language, _namespace, _data) {
    // Not implemented for production
  };
  return ManifestAwareBackend;
}();

// Export factory function for easier integration
export function createManifestAwareBackend(_options) {
  return new ManifestAwareBackend();
}
export default ManifestAwareBackend;